/**
 * Opportunity Detail Page
 *
 * Displays opportunity details with status management,
 * conversion to project, and client links.
 *
 * @module pages/opportunities/OpportunityDetail
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  getOpportunity,
  updateOpportunity,
  convertToProject,
  STATUS_PROBABILITY,
} from '../../services/opportunityService';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';

const STATUS_CONFIG = {
  lead: { label: 'Lead', variant: 'secondary' },
  prospect: { label: 'Prospecto', variant: 'info' },
  qualified: { label: 'Calificado', variant: 'primary' },
  proposal: { label: 'Propuesta', variant: 'warning' },
  negotiation: { label: 'Negociacion', variant: 'dark' },
  won: { label: 'Ganada', variant: 'success' },
  lost: { label: 'Perdida', variant: 'danger' },
};

const SOURCE_LABELS = {
  referral: 'Referencia',
  website: 'Sitio Web',
  cold_call: 'Llamada en Frio',
  trade_show: 'Feria Comercial',
  repeat_client: 'Cliente Recurrente',
  other: 'Otro',
};

const STATUS_ORDER = ['lead', 'prospect', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

const OpportunityDetail = () => {
  const navigate = useNavigate();
  const { id: opportunityId } = useParams();
  const queryClient = useQueryClient();
  const { isAdmin, isTechnician } = useAuth();
  const canEdit = isAdmin || isTechnician;

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState('');

  const { data: oppData, isLoading, error } = useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => getOpportunity(opportunityId),
    enabled: !!opportunityId,
  });

  const opportunity = oppData?.data;

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'lost') {
      setShowLostModal(true);
      return;
    }

    try {
      const updates = {
        status: newStatus,
        probability_percentage: STATUS_PROBABILITY[newStatus] ?? opportunity.probability_percentage,
      };
      const result = await updateOpportunity(opportunityId, updates);
      if (result.error) throw result.error;
      toast.success(`Estado actualizado a ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      queryClient.invalidateQueries(['opportunity', opportunityId]);
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleMarkLost = async () => {
    try {
      const result = await updateOpportunity(opportunityId, {
        status: 'lost',
        probability_percentage: 0,
        lost_reason: lostReason,
      });
      if (result.error) throw result.error;
      toast.success('Oportunidad marcada como perdida');
      setShowLostModal(false);
      setLostReason('');
      queryClient.invalidateQueries(['opportunity', opportunityId]);
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const result = await updateOpportunity(opportunityId, {
        title: editData.title,
        description: editData.description,
        estimated_value: parseFloat(editData.estimated_value) || null,
        expected_close_date: editData.expected_close_date || null,
        probability_percentage: parseInt(editData.probability_percentage) || 0,
        notes: editData.notes,
      });
      if (result.error) throw result.error;
      toast.success('Oportunidad actualizada');
      setIsEditing(false);
      queryClient.invalidateQueries(['opportunity', opportunityId]);
    } catch (err) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async () => {
    try {
      setConverting(true);
      const result = await convertToProject(opportunityId);
      if (result.error) throw result.error;
      toast.success('Proyecto creado exitosamente');
      setShowConvertModal(false);
      navigate(`/proyectos/${result.data.id}`);
    } catch (err) {
      toast.error(err.message || 'Error al convertir a proyecto');
    } finally {
      setConverting(false);
    }
  };

  const startEditing = () => {
    setEditData({
      title: opportunity.title || '',
      description: opportunity.description || '',
      estimated_value: opportunity.estimated_value || '',
      expected_close_date: opportunity.expected_close_date || '',
      probability_percentage: opportunity.probability_percentage || 0,
      notes: opportunity.notes || '',
    });
    setIsEditing(true);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" />
        </Container>
      </DashboardLayout>
    );
  }

  if (error || !opportunity) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar oportunidad</Alert.Heading>
            <p>{error?.message || 'Oportunidad no encontrada'}</p>
            <Button variant="outline-danger" onClick={() => navigate('/oportunidades')}>
              Volver a Oportunidades
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  const sc = STATUS_CONFIG[opportunity.status] || STATUS_CONFIG.lead;

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <Button
                  variant="link"
                  className="p-0 mb-2 text-muted"
                  onClick={() => navigate('/oportunidades')}
                >
                  <i className="bi bi-arrow-left me-1"></i>Volver a Oportunidades
                </Button>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h2 className="mb-0">{opportunity.title}</h2>
                  <Badge bg={sc.variant}>{sc.label}</Badge>
                </div>
                {opportunity.client?.company_name && (
                  <p className="mb-0 text-muted">
                    <i className="bi bi-building me-2"></i>
                    {opportunity.client.company_name}
                  </p>
                )}
              </div>
              <div className="d-flex gap-2">
                {canEdit && !isEditing && (
                  <Button variant="outline-primary" size="sm" onClick={startEditing}>
                    <i className="bi bi-pencil me-1"></i>Editar
                  </Button>
                )}
                {isAdmin && opportunity.status === 'won' && !opportunity.project_id && (
                  <Button variant="success" size="sm" onClick={() => setShowConvertModal(true)}>
                    <i className="bi bi-arrow-right-circle me-1"></i>Convertir a Proyecto
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          {/* Main Content */}
          <Col lg={8} className="mb-4">
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Detalle</h5>
              </Card.Header>
              <Card.Body>
                {isEditing ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Titulo</Form.Label>
                      <Form.Control
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Descripción</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={editData.description}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </Form.Group>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Valor Estimado</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={editData.estimated_value}
                            onChange={(e) => setEditData(prev => ({ ...prev, estimated_value: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Probabilidad (%)</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            value={editData.probability_percentage}
                            onChange={(e) => setEditData(prev => ({ ...prev, probability_percentage: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Fecha de Cierre Esperada</Form.Label>
                          <Form.Control
                            type="date"
                            value={editData.expected_close_date}
                            onChange={(e) => setEditData(prev => ({ ...prev, expected_close_date: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Notas</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={editData.notes}
                        onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
                        {saving ? 'Guardando...' : 'Guardar'}
                      </Button>
                      <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={saving}>
                        Cancelar
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {opportunity.description && (
                      <div className="mb-3">
                        <small className="text-muted d-block">Descripción</small>
                        <p>{opportunity.description}</p>
                      </div>
                    )}

                    <Row className="mb-3">
                      <Col md={4}>
                        <small className="text-muted d-block">Valor Estimado</small>
                        <strong className="fs-5 text-primary">
                          {formatCurrency(opportunity.estimated_value)}
                        </strong>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Probabilidad</small>
                        <strong>{formatPercentage(opportunity.probability_percentage)}</strong>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Cierre Esperado</small>
                        <strong>{formatDate(opportunity.expected_close_date)}</strong>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <small className="text-muted d-block">Fuente</small>
                        <span>{SOURCE_LABELS[opportunity.source] || opportunity.source || '-'}</span>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Asignado a</small>
                        <span>{opportunity.assigned?.full_name || '-'}</span>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Creado</small>
                        <span>{formatDate(opportunity.created_at)}</span>
                      </Col>
                    </Row>

                    {opportunity.notes && (
                      <div className="mb-3">
                        <small className="text-muted d-block">Notas</small>
                        <p className="mb-0">{opportunity.notes}</p>
                      </div>
                    )}

                    {opportunity.lost_reason && (
                      <div className="bg-danger bg-opacity-10 p-3 rounded">
                        <small className="text-muted d-block">Razon de Perdida</small>
                        <p className="mb-0">{opportunity.lost_reason}</p>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Links */}
            {(opportunity.project || opportunity.quotation) && (
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Vinculos</h6>
                </Card.Header>
                <Card.Body>
                  {opportunity.project && (
                    <Button
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => navigate(`/proyectos/${opportunity.project.id}`)}
                    >
                      <i className="bi bi-folder me-1"></i>
                      Proyecto: {opportunity.project.project_number}
                    </Button>
                  )}
                  {opportunity.quotation && (
                    <Button
                      variant="outline-info"
                      onClick={() => navigate(`/cotizaciones/${opportunity.quotation.id}`)}
                    >
                      <i className="bi bi-file-text me-1"></i>
                      Cotización: {opportunity.quotation.quotation_number}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Sidebar - Status Pipeline */}
          <Col lg={4}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Pipeline de Estado</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-column gap-2">
                  {STATUS_ORDER.map((status) => {
                    const cfg = STATUS_CONFIG[status];
                    const isCurrent = opportunity.status === status;
                    const isDisabled = !canEdit || opportunity.status === 'won' || opportunity.status === 'lost';

                    return (
                      <Button
                        key={status}
                        variant={isCurrent ? cfg.variant : `outline-${cfg.variant}`}
                        size="sm"
                        className="text-start"
                        disabled={isCurrent || isDisabled}
                        onClick={() => handleStatusChange(status)}
                      >
                        <i className={`bi ${isCurrent ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                        {cfg.label}
                        <small className="ms-auto float-end text-muted">
                          {formatPercentage(STATUS_PROBABILITY[status])}
                        </small>
                      </Button>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>

            {/* Client info */}
            {opportunity.client && (
              <Card className="mt-3">
                <Card.Header>
                  <h6 className="mb-0">Cliente</h6>
                </Card.Header>
                <Card.Body>
                  <p className="fw-bold mb-1">{opportunity.client.company_name}</p>
                  {opportunity.client.primary_contact_email && (
                    <small className="text-muted d-block">
                      <i className="bi bi-envelope me-1"></i>
                      {opportunity.client.primary_contact_email}
                    </small>
                  )}
                  {opportunity.client.primary_contact_phone && (
                    <small className="text-muted d-block">
                      <i className="bi bi-telephone me-1"></i>
                      {opportunity.client.primary_contact_phone}
                    </small>
                  )}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate(`/clientes/${opportunity.client.id}`)}
                  >
                    Ver Cliente
                  </Button>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Convert to Project Modal */}
        <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Convertir a Proyecto</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Se creara un nuevo proyecto basado en esta oportunidad:</p>
            <div className="bg-light p-3 rounded">
              <strong>{opportunity.title}</strong>
              {opportunity.client?.company_name && (
                <small className="d-block text-muted">{opportunity.client.company_name}</small>
              )}
              {opportunity.estimated_value && (
                <small className="d-block text-muted">
                  Valor: {formatCurrency(opportunity.estimated_value)}
                </small>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConvertModal(false)} disabled={converting}>
              Cancelar
            </Button>
            <Button variant="success" onClick={handleConvert} disabled={converting}>
              {converting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />
                  Convirtiendo...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-right-circle me-1"></i>
                  Crear Proyecto
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Mark as Lost Modal */}
        <Modal show={showLostModal} onHide={() => setShowLostModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Marcar como Perdida</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Razon de la perdida</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                placeholder="Ej: Precio muy alto, competencia gano, presupuesto del cliente..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLostModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleMarkLost}>
              <i className="bi bi-x-circle me-1"></i>Marcar como Perdida
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default OpportunityDetail;
